import { useParams } from 'react-router-dom';
import {
    customerSalesIcon,
    geographyIcon,
    globeGray,
    infoBlue,
    infoPurpleIcon,
    linkedinIconWhite,
    locationPinGray,
    plusBlack,
    productOfferingIcon,
    vendorsIcon,
    xTwitterIconWhite,
} from '../../../../../../assets/hostedassets';
import Typewriter from '../../../../../../common/Typewriter/Typewriter';
import {
    AICA_OVERVIEW_SEE_MORE_CLICKED,
    INVESTOR_OPEN_COMPANY_WEBSITE,
} from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { getTimeStamp } from '../../../../../../utils/dateUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { ensureHttpsPrefix, isEmpty } from '../../../../../../utils/utils';
import styles from './Details.module.scss';
import { useEffect, useState } from 'react';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import AicaOverview from '../../../../../../common/AicaOverview/AicaOverview';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { setDealDrawer } from '../../../../../../store/investor/action';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../../../../../app/hooks';
import { USER_TYPE } from '../../../../../../utils/constants/user';
import { getFormerEntityData } from '../../../../../../store/investor/apiHelpers';
import dayjs from 'dayjs';
import { getCompanyLogo, getFaviconUrl } from '../../../../../../utils/urlMetadata';
import { stripHtml, splitBullets } from '../../../../../AicaReportV2/data/overviewHelpers';
import ExpandableText from '../../../../../../common/ExpandableText/ExpandableText';
import AlertEngine from '../../../../components/AlertEngine/AlertEngine';
import CompaniesNetworkButton from '../../../../components/CompaniesNetwork/CompaniesNetworkButton';
import BasicDetailsSidebar from '../BasicDetailsSidebar/BasicDetailsSidebar';
import MetricsKPISidebar from '../MetricsKPISidebar/MetricsKPISidebar';
import AICAGradeV2 from '../AICAGradeV2/AICAGradeV2';
import { aicaBg1, aicaBg4, aicaBgDefault } from '../../../../../../assets/hostedassets';
import { ACCESS_TIERS } from '../../../../../../store/investor/constants';
import UnlockOverviewSection from '../UnlockOverviewSection/UnlockOverviewSection';
import { useVisibilityConfig, useFeatureVisibility } from '../../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../../utils/constants/visibilityKeys';
import { TabModule } from '../../../../../../utils/constants/commonConstants';

interface FormerEntity {
    name: string;
    date: string;
}

const OverviewBulletsSection = ({ content, selectedCompany }: { content: string; selectedCompany?: any }) => (
    <ExpandableText
        content={content}
        onExpand={() =>
            Mixpanel.track(AICA_OVERVIEW_SEE_MORE_CLICKED, {
                Timestamp: getTimeStamp(),
                companyName: selectedCompany?.orgName,
                AICASection: selectedCompany?.companyAccessType,
            })
        }
    />
);

function Details({ overviewData, accessType, selectedCompany, isPDFReport, overviewDataV2, leftSlot, rightSlot, navigateToTab }) {
    const {
        sectorInfo,
        overview,
        businessModel,
        socialInfo,
        foundationDate,
        headquarterLocation,
        fundingInfo,
        employeeData,
        cinNumber,
        panNumber,
        gstin,
    } = overviewData;

    const params = useParams();
    const [formerEntityData, setFormerEntityData] = useState<FormerEntity[]>([]);
    const [forceHideFormer, setForceHideFormer] = useState(false);
    const user = useAppSelector((state) => state.user.userData);
    const showAskAica = useAppSelector((state) => state.dashboard.dashboardReducer.config?.tabConfigs?.overview?.showAskAica);
    const isValidUser = [USER_TYPE.INVESTOR, USER_TYPE.ADMIN_TEAM].includes(user?.accountType);
    const dispatch = useDispatch();

    const { config: visibilityConfig } = useVisibilityConfig();
    const { sidebarRelatedNetworkVisibility } = useFeatureVisibility({
        sidebarRelatedNetworkVisibility: VISIBILITY_KEYS.SIDEBAR.RELATED_NETWORK_ENABLE,
    });
    const tabLockStatus = useAppSelector((state) => state.investor.tabLockStatus);
    const isRelatedPartyLocked = !!(tabLockStatus as any)?.[TabModule.RELATED_PARTY]?.isLocked;

    const additionalDetails = (cinNumber && cinNumber !== '') || (panNumber && panNumber !== '') || gstin?.length > 0;
    const bodyHasData = !!(
        sectorInfo?.sector ||
        sectorInfo?.subSector ||
        !!overview ||
        !!businessModel ||
        !!headquarterLocation?.fullAddress ||
        additionalDetails
    );

    useEffect(() => {
        fetchFormerEntityData();
    }, [params.id]);

    const fetchFormerEntityData = () => {
        getFormerEntityData(
            { organizationId: params.id || '' },
            {
                onSuccess: (data: FormerEntity[]) => {
                    setFormerEntityData(data);
                },
            },
        );
    };

    const onWebsiteClick = () => {
        Mixpanel.track(INVESTOR_OPEN_COMPANY_WEBSITE, {
            Timestamp: getTimeStamp(),
            AICASection: accessType,
        });
    };

    function formatDate(inputDate: string): string {
        let date: Date;
        if (inputDate.includes('T')) {
            // ISO format: "2018-02-20T00:00:00+05:30"
            date = new Date(inputDate);
        } else {
            // DD-MM-YYYY format
            const [day, month, year] = inputDate.split('-');
            date = new Date(`${year}-${month}-${day}`);
        }

        const options: Intl.DateTimeFormatOptions = {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    }

    const getFormerEntityTooltipContent = (formerEntities: FormerEntity[]) => {
        return (
            <div>
                {formerEntities?.map((entity, index) => (
                    <div className={styles.FormerEntityTooltip} key={`${entity.name}-${index}`}>
                        <div className={styles.FormerEntityName}>
                            <span>{entity.name}</span>
                        </div>
                        {entity.date && <div className={styles.FormerEntityDate}>(Till {dayjs(entity.date).format('DD MMM YYYY')})</div>}
                        {index !== formerEntities.length - 1 && <div className={styles.FormerEntitySeparator}></div>}
                    </div>
                ))}
            </div>
        );
    };

    const openEditProfileDrawer = () => {
        dispatch(
            setDealDrawer({
                open: true,
                drawerLabel: 'Edit Profile',
                drawerSource: 'Debt Products',
                selectedStep: 2,
            }),
        );
    };

    const createPlaceholder = (parent: HTMLElement) => {
        const placeholder = document.createElement('div');
        placeholder.className = styles.PlaceholderImg;
        placeholder.textContent = selectedCompany?.orgName?.charAt(0) || '';
        parent.appendChild(placeholder);
    };

    const handleLogoError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        e.currentTarget.style.display = 'none';
        const parent = e.currentTarget.parentElement;
        if (!parent) return;

        const websiteUrl = socialInfo?.websiteUrl || selectedCompany?.websiteUrl;
        const faviconUrl = websiteUrl && getFaviconUrl(websiteUrl);
        const currentSrc = e.currentTarget.src;

        if (faviconUrl && currentSrc !== faviconUrl) {
            const fallbackImg = document.createElement('img');
            fallbackImg.src = faviconUrl;
            fallbackImg.alt = 'Company Logo';
            fallbackImg.className = styles.LogoImg;
            fallbackImg.onerror = () => {
                fallbackImg.style.display = 'none';
                createPlaceholder(parent);
            };
            parent.appendChild(fallbackImg);
        } else {
            createPlaceholder(parent);
        }
    };

    const OVERVIEW_BGS = [aicaBg1, aicaBg4, aicaBgDefault];
    const bgIndex = (params.id || '').split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % OVERVIEW_BGS.length;

    const renderTopSection = () => (
        <div className={styles.TopWrapper}>
            <div
                className={clsx(styles.Backdrop, { [styles.BackdropPDF]: isPDFReport })}
                style={!isPDFReport ? { backgroundImage: `url(${OVERVIEW_BGS[bgIndex]})` } : undefined}
            />
            <div className={styles.TopGrid}>
                <div className={styles.CompanyCard}>
                    <div className={styles.CardInner}>
                        <div className={styles.CardTopRow}>
                            <div className={styles.CompanyLogo}>
                                {(() => {
                                    const companyLogo = selectedCompany?.logo;
                                    const websiteUrl = socialInfo?.websiteUrl || selectedCompany?.websiteUrl;
                                    const logoFromWebsite = websiteUrl && getCompanyLogo(websiteUrl);
                                    const faviconFromWebsite = websiteUrl && getFaviconUrl(websiteUrl);
                                    const logoSrc = companyLogo || logoFromWebsite || faviconFromWebsite;

                                    return logoSrc ? (
                                        <img src={logoSrc} alt="Company Logo" className={styles.LogoImg} onError={handleLogoError} />
                                    ) : (
                                        <div className={styles.PlaceholderImg}>{selectedCompany?.orgName?.charAt(0) || ''}</div>
                                    );
                                })()}
                            </div>
                            <div className={styles.CompanyInfo}>
                                <div className={styles.Title}>
                                    <Typewriter text={selectedCompany?.orgName} delay={20} />
                                </div>
                                <div className={styles.MetaRow}>
                                    {!!(headquarterLocation?.city || headquarterLocation?.state) && (
                                        <div className={styles.MetaItem}>
                                            <img src={locationPinGray} alt="" className={styles.LocationIcon} />
                                            <span>
                                                {headquarterLocation?.city}
                                                {headquarterLocation?.city && headquarterLocation?.state && ', '}
                                                {headquarterLocation?.state}
                                            </span>
                                        </div>
                                    )}
                                    {!!socialInfo?.websiteUrl ? (
                                        <a
                                            className={clsx(styles.MetaItem, styles.MetaItemLink)}
                                            href={ensureHttpsPrefix(socialInfo.websiteUrl)}
                                            target="_blank"
                                            onClick={onWebsiteClick}
                                        >
                                            <img src={globeGray} alt="" className={styles.GlobeIcon} />
                                            <span>{socialInfo.websiteUrl.replace(/^https?:\/\//, '')}</span>
                                        </a>
                                    ) : (
                                        isValidUser && (
                                            <div className={clsx(styles.MetaItem, styles.MetaItemLink)} onClick={openEditProfileDrawer}>
                                                <img src={plusBlack} alt="" className={styles.AddIcon} />
                                                <span>Add Website</span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                            {!isPDFReport && (
                                <div className={styles.SocialIcons}>
                                    {(socialInfo?.linkedinUrl || socialInfo?.userInputLinkedin) && (
                                        <a
                                            className={styles.SocialIcon}
                                            href={ensureHttpsPrefix(socialInfo?.linkedinUrl || socialInfo?.userInputLinkedin)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img src={linkedinIconWhite} alt="LinkedIn" />
                                        </a>
                                    )}
                                    {socialInfo?.twitterUrl && (
                                        <a
                                            className={styles.SocialIcon}
                                            href={ensureHttpsPrefix(socialInfo.twitterUrl)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <img src={xTwitterIconWhite} alt="Twitter" />
                                        </a>
                                    )}
                                    {isValidUser && !(socialInfo?.linkedinUrl || socialInfo?.userInputLinkedin) && (
                                        <Tooltip
                                            content="Add LinkedIn"
                                            direction="bottom"
                                            small
                                            fitContent
                                            contentCss={{ whiteSpace: 'nowrap' }}
                                        >
                                            <div className={clsx(styles.SocialIcon, styles.SocialIconAdd)} onClick={openEditProfileDrawer}>
                                                <img src={plusBlack} alt="Add" className={styles.AddIcon} />
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            )}
                        </div>

                        {!isPDFReport && showAskAica && (
                            <div
                                className={styles.AskAicaInput}
                                onClick={() => {
                                    const globalIcon = document.querySelector('[class*="GlobalIcon"]') as HTMLElement;
                                    if (globalIcon) globalIcon.click();
                                }}
                            >
                                <span className={styles.AskAicaPlaceholder}>Ask anything</span>
                            </div>
                        )}
                    </div>
                </div>

                {!isPDFReport && (
                    <div className={styles.ActivityCards}>
                        <AlertEngine selectedCompany={selectedCompany} currentTab="overview" variant="card" />
                        {visibilityConfig && sidebarRelatedNetworkVisibility.show && (
                            <CompaniesNetworkButton
                                companyName={selectedCompany?.orgName}
                                isRelatedPartiesLocked={isRelatedPartyLocked}
                                variant="card"
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    const isOverviewLocked = overviewData?.overallStatus === 'LOCKED';

    const aiCards = [
        {
            key: 'productOfferings',
            title: 'Product/ Service Offering:',
            content: overviewDataV2?.overview?.productOfferings,
            icon: productOfferingIcon,
        },
        {
            key: 'customersAndSalesChannels',
            title: 'Customer Sales & Channels',
            content: overviewDataV2?.overview?.customersAndSalesChannels,
            icon: customerSalesIcon,
        },
        {
            key: 'vendorsSourcingManufacturing',
            title: 'Vendors, Sourcing & Manufacturing',
            content: overviewDataV2?.overview?.vendorsSourcingManufacturing,
            icon: vendorsIcon,
        },
        {
            key: 'geographiesSegmentation',
            title: 'Geographies / Segmentation',
            content: overviewDataV2?.overview?.geographiesSegmentation,
            icon: geographyIcon,
        },
    ].filter((card) => !!card.content);

    const tradeNames: string[] = overviewDataV2?.tradeNames ?? [];
    const gstTurnover =
        overviewDataV2?.latestFy && overviewDataV2?.latestFyTurnoverBand
            ? { fy: overviewDataV2.latestFy, band: overviewDataV2.latestFyTurnoverBand }
            : null;

    const renderCompanyDetails = () => {
        const sectorValue = overviewDataV2?.sectorDetails?.sector || sectorInfo?.sector;
        const foundedValue = foundationDate ? formatDate(foundationDate) : null;
        const revenueValue = gstTurnover ? gstTurnover.band : overviewDataV2?.overview?.revenue;
        const keyInvestorsValue = overviewDataV2?.overview?.keyInvestors || (fundingInfo?.keyInvestors ? [fundingInfo.keyInvestors] : null);
        const fundingRaisedValue = overviewDataV2?.overview?.fundingRaised || (fundingInfo?.stage ?? null);
        const totalEmployeesValue = employeeData?.currentEmployeeRange || employeeData?.currentEmployeeCount;
        const overviewContent = overviewDataV2?.overview?.overviewContent || overview;

        const hasGridData = sectorValue || foundedValue || revenueValue || keyInvestorsValue || fundingRaisedValue || totalEmployeesValue;
        const hasContent = hasGridData || overviewContent;

        if (!hasContent && !isOverviewLocked) return null;

        return (
            <>
                <div className={styles.BottomWrap}>
                    <div className={styles.Header}>
                        <div className={styles.Heading}>Company Overview</div>
                        <div className={styles.Right}>
                            {tradeNames.length > 0 && !isPDFReport && (
                                <div onMouseEnter={() => setForceHideFormer(true)} onMouseLeave={() => setForceHideFormer(false)}>
                                    <Tooltip
                                        content={
                                            <div>
                                                {tradeNames.map((name, index) => (
                                                    <div className={styles.FormerEntityTooltip} key={`trade-${index}`}>
                                                        <div className={styles.FormerEntityName}>
                                                            <span>{name}</span>
                                                        </div>
                                                        {index !== tradeNames.length - 1 && (
                                                            <div className={styles.FormerEntitySeparator}></div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        }
                                        direction="bottom"
                                        hideDelay={500}
                                        isOverviewDetail={true}
                                        style={{ width: 'max-content', minWidth: 0, maxWidth: '26.25rem' }}
                                    >
                                        <div className={styles.HeaderPill}>
                                            {tradeNames.length} Trade {tradeNames.length === 1 ? 'Name' : 'Names'}
                                            <img src={infoPurpleIcon} alt="" className={styles.HeaderPillIcon} />
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                            {!isEmpty(formerEntityData) && (
                                <Tooltip
                                    content={forceHideFormer ? '' : getFormerEntityTooltipContent(formerEntityData)}
                                    direction="bottom"
                                    hideDelay={forceHideFormer ? 0 : 500}
                                    isOverviewDetail={true}
                                    style={{ width: 'max-content', minWidth: 0, maxWidth: '26.25rem' }}
                                    contentCss={{ maxHeight: '8rem', overflowY: 'auto' }}
                                >
                                    <div className={clsx(styles.HeaderPill, styles.HeaderPillFormer)}>
                                        {formerEntityData.length} Former {formerEntityData.length === 1 ? 'Name' : 'Names'}
                                        <img src={infoBlue} alt="" className={styles.HeaderPillIcon} />
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className={styles.Bottom}>
                        {isOverviewLocked ? (
                            <UnlockOverviewSection overviewData={overviewData} />
                        ) : (
                            <>
                                {hasGridData && (
                                    <div className={styles.DetailsGrid}>
                                        {sectorValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Sector</span>
                                                <span className={styles.DetailValue}>{sectorValue}</span>
                                            </div>
                                        )}
                                        {foundedValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Founded</span>
                                                <span className={styles.DetailValue}>{foundedValue}</span>
                                            </div>
                                        )}
                                        {revenueValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>FY'24-25 Revenue</span>
                                                <span className={styles.DetailValue}>{revenueValue}</span>
                                            </div>
                                        )}
                                        {keyInvestorsValue && keyInvestorsValue.length > 0 && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Key Investors</span>
                                                <span className={styles.DetailValue}>
                                                    {keyInvestorsValue[0]}
                                                    {keyInvestorsValue.length > 1 && (
                                                        <Tooltip
                                                            content={keyInvestorsValue.slice(1).join(', ')}
                                                            direction="top"
                                                            isOverviewDetail={true}
                                                            fitContent={true}
                                                            usePortal={true}
                                                            hideDelay={500}
                                                        >
                                                            <span className={styles.MoreLink}> +{keyInvestorsValue.length - 1} more</span>
                                                        </Tooltip>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        {fundingRaisedValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Funding Raised</span>
                                                <span className={styles.DetailValue}>{fundingRaisedValue}</span>
                                            </div>
                                        )}
                                        {totalEmployeesValue && (
                                            <div className={styles.DetailField}>
                                                <span className={styles.DetailLabel}>Total Employees</span>
                                                <span className={styles.DetailValue}>{totalEmployeesValue}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {overviewContent && <OverviewBulletsSection content={overviewContent} selectedCompany={selectedCompany} />}
                            </>
                        )}
                    </div>
                </div>

                {/* AI Business Model Cards — hidden when locked */}
                {!isOverviewLocked && aiCards.length > 0 && (
                    <div className={styles.AICardsGrid}>
                        {aiCards.map((card) => (
                            <div key={card.key} className={styles.AICard}>
                                <div className={styles.AICardTitle}>
                                    <img src={card.icon} alt="" width="20" height="20" />
                                    <span>{card.title}</span>
                                </div>
                                <ul className={styles.AICardContent}>
                                    {splitBullets(stripHtml(card.content)).map((bullet, i) => (
                                        <li key={i}>{bullet.replace(/^[•·\-–—]\s*/, '').trim()}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    const render = (
        <div className={styles.Details} style={{ position: 'relative' }}>
            {renderTopSection()}
            <AicaOverview AicaOverviewBodyStyle={{ border: '1px solid #e7e7e7' }} style={{ marginBottom: '1.5rem' }} module="overview" />
            {bodyHasData ? (
                <div className={styles.BentoGrid}>
                    <div className={styles.BentoLeft}>
                        {renderCompanyDetails()}
                        {leftSlot}
                    </div>
                    {!isPDFReport && (
                        <div className={styles.BentoRight}>
                            <MetricsKPISidebar orgId={params.id} navigateToTab={navigateToTab} isLocked={isOverviewLocked} />
                            {rightSlot}
                            {!isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={false} />}
                            <BasicDetailsSidebar
                                panNumber={panNumber}
                                cinNumber={cinNumber}
                                gstin={gstin}
                                headquarterLocation={headquarterLocation}
                            />
                        </div>
                    )}
                </div>
            ) : (
                !isPDFReport && !isOverviewLocked && accessType !== ACCESS_TIERS.WATCH && <AICAGradeV2 isPDFReport={false} />
            )}
        </div>
    );

    if (isPDFReport)
        return reportSectionsToShow?.includes(REPORT_SECTION_NAMES.OVERVIEW) ? (
            <>
                <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                    <div className={clsx(styles.Details, styles.PDFReport)}>
                        {renderTopSection()}
                        <AicaOverview style={{ marginTop: '0px', marginBottom: '24px' }} module={'overview'} isPDFReport={isPDFReport} />
                        {/* <>{renderCompanyDetails()}</> */}
                    </div>
                </A4Page>
                {(bodyHasData || additionalDetails) && (
                    <A4Page sectionName={REPORT_SECTION_NAMES.OVERVIEW}>
                        <div className={clsx(styles.Details, styles.PDFReport)}>
                            {renderCompanyDetails()}
                            <BasicDetailsSidebar
                                panNumber={panNumber}
                                cinNumber={cinNumber}
                                gstin={gstin}
                                headquarterLocation={headquarterLocation}
                            />
                        </div>
                    </A4Page>
                )}
            </>
        ) : (
            <></>
        );
    else return render;
}

export default Details;
